import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { ArrowRightCircle } from 'react-bootstrap-icons';
import format from 'date-fns/format';

// Demo-specific column components
function DemoUpdatedAtColumn({ props }) {
  try {
    let date = new Date(props.updatedAt);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    let dateTime = format(date, 'dd/MM/yy');
    return dateTime;
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
}

function DemoCreatedAtColumn({ props }) {
  try {
    let date = new Date(props.createdAt);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    let dateTime = format(date, 'dd/MM/yy');
    return dateTime;
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
}

function DemoManagerColumn({ props }) {
  const name = props.isOrganiserManager === true ? props.organiserName : props.attendeeName;
  const creator = props.isOrganiserManager === true ? '*' : '';
  return name + creator;
}

function DemoTeamMemberColumn({ props }) {
  const name = props.isOrganiserManager !== true ? props.organiserName : props.attendeeName;
  const creator = props.isOrganiserManager !== true ? '*' : '';
  return name + creator;
}

function DemoEmployeePrepColumn(props) {
  const navigate = useNavigate();
  
  let statusString, buttonString, conversationLink, className;

  // IDENTICAL PRODUCTION LOGIC: Status and button text based on employeePrepStatus
  switch (props.employeePrepStatus) {
    case 0:
      buttonString = 'Start';
      statusString = 'Not Started';
      className = 'pending';
      break;
    case 1:
      buttonString = 'Continue';
      statusString = 'In Progress';
      className = 'in-progress';
      break;
    case 2:
      if (props.isCanvasData || props.isUserManager) {
        buttonString = 'View';
        statusString = 'Complete';
        className = 'complete';
        break;
      } else {
        buttonString = 'Edit';
        statusString = 'Complete';
        className = 'complete';
        break;
      }
    default:
      statusString = 'unknown';
      buttonString = 'View';
      className = 'pending';
  }

  // IDENTICAL PRODUCTION LOGIC: Determine if conversationLink should exist
  switch (props.employeePrepStatus) {
    case 0:
      conversationLink = !props.isUserManager
        ? `/conversations/${props.id}/team-member-prep/homes`
        : '';
      break;
    case 1:
      conversationLink = !props.isUserManager
        ? `/conversations/${props.id}/categories/edit`
        : '';
      break;
    case 2:
      conversationLink = !props.isUserManager
        ? `/conversations/${props.id}/categories/edit`
        : `/conversations/${props.id}/managers`;
      break;
    default:
      conversationLink = '';
  }

  // Demo click handler for employee prep - navigate to proper demo page
  const handleDemoClick = () => {
    // If this is a manager viewing completed prep, navigate to demo manager prep page
    if (props.isUserManager && props.employeePrepStatus === 2) {
      navigate(`/demo/conversations/${props.id}/managers`);
    } else {
      // For other cases, show alert as before
      alert(`Demo Action: This would open the ${statusString.toLowerCase()} preparation steps for ${props.attendeeName}. In the full app, this would navigate to the team member preparation page.`);
    }
  };

  // IDENTICAL PRODUCTION STRUCTURE: Create button element
  let button = (
    <span className={`data-table__faux-button data-table__faux-button--${className}`}>
      {buttonString}
      <ArrowRightCircle size={16} className="data-table__icon" />
    </span>
  );

  // IDENTICAL PRODUCTION LOGIC: Only show button if conversationLink exists
  if (conversationLink) {
    return (
      <span 
        className={`data-table__icon-link data-table__icon-link--${className}`}
        onClick={handleDemoClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            handleDemoClick();
          }
        }}
        style={{ cursor: 'pointer' }}
        data-demo-id={`demo-prep-${props.id}`}
        aria-label={`${buttonString} preparation for ${props.attendeeName}`}
        role="button"
        tabIndex={0}
      >
        <span className="data-table__status-string">{statusString}</span>
        {button}
      </span>
    );
  } else {
    return (
      <span className={`data-table__icon-link data-table__icon-link--${className}`}>
        <span className="data-table__status-string">{statusString}</span>
      </span>
    );
  }
}

const DemoStatusColumn = ({ props }) => {
  const navigate = useNavigate();
  let statusString, className, button, buttonString = 'View';

  switch (props.status) {
    case 0:
      statusString = 'Not Started';
      className = 'pending';
      break;
    case 1:
      statusString = 'In Progress';
      className = 'in-progress';
      break;
    case 2:
      statusString = 'Complete';
      className = 'complete';
      break;
    case 3:
      statusString = 'Deleted';
      className = 'deleted';
      break;
    default:
      statusString = 'unknown';
      className = 'pending';
  }

  // Button text logic based on canvas data and status
  if (props.status === 2) {
    buttonString = 'View';
  } else if (!props.isCanvasData) {
    buttonString = 'Start';
  } else if (props.isCanvasData && props.status < 2) {
    buttonString = 'Continue';
  }

  const handleDemoClick = () => {
    // If this is the completed demo conversation, navigate to completed canvas
    if (props.id === 'demo-completed-conversation' && props.status === 2) {
      navigate('/demo/canvas/completed');
    } else {
      // Use React Router navigation to stay in demo mode for other conversations
      navigate('/demo/canvas');
    }
  };

  // MODIFIED LOGIC: Only show button if user is manager (not for completed conversations for team members)
  if (props.isUserManager === true) {
    button = (
      <span 
        className="data-table__faux-button"
        onClick={handleDemoClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            handleDemoClick();
          }
        }}
        style={{ cursor: 'pointer' }}
        data-demo-id={`demo-conversation-${props.id}`}
        aria-label={`${buttonString} conversation for ${props.attendeeName}`}
        role="button"
        tabIndex={0}
      >
        {buttonString}
        <ArrowRightCircle size={16} className="data-table__icon" />
      </span>
    );
  }

  return (
    <span className={`data-table__icon-link data-table__icon-link--${className}`}>
      <span className="data-table__status-string">{statusString}</span>
      {button}
    </span>
  );
};

// Shared shape for row props used in all columns
const demoRowPropShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  organiserName: PropTypes.string,
  attendeeName: PropTypes.string,
  isOrganiserManager: PropTypes.bool,
  isUserManager: PropTypes.bool,
  isCanvasData: PropTypes.bool,
  employeePrepStatus: PropTypes.number,
  status: PropTypes.number,
  updatedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
  createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)])
});

// Demo data table columns configuration
export const demoDataTableColumns = [
  {
    name: 'Manager',
    selector: (row) => (row.isOrganiserManager === true ? row.organiserName : row.attendeeName),
    cell: (props) => DemoManagerColumn({ props }),
    sortable: true,
    fixed: true,
    reorder: false,
    width: '215px',
  },
  {
    name: 'Team member',
    selector: (row) => (row.isOrganiserManager !== true ? row.organiserName : row.attendeeName),
    cell: (props) => DemoTeamMemberColumn({ props }),
    sortable: true,
    fixed: true,
    reorder: false,
    width: '215px',
  },
  {
    name: 'Created',
    selector: (row) => row.createdAt,
    cell: (props) => DemoCreatedAtColumn({ props }),
    sortable: true,
    reorder: false,
    fixed: true,
    width: '110px',
  },
  {
    name: 'Updated',
    selector: (row) => row.updatedAt,
    cell: (props) => DemoUpdatedAtColumn({ props }),
    sortable: true,
    reorder: false,
    fixed: true,
    width: '110px',
  },
  {
    name: 'Preparation',
    selector: (row) => row.employeePrepStatus,
    cell: (props) => DemoEmployeePrepColumn(props),
    sortable: true,
    reorder: false,
    fixed: true,
    width: '260px',
  },
  {
    name: 'Conversation',
    selector: (row) => row.status,
    cell: (props) => <DemoStatusColumn props={props} />,
    sortable: true,
    reorder: false,
    fixed: true,
    width: '260px',
  },
];

// PropTypes for the column functions
DemoUpdatedAtColumn.propTypes = {
  props: demoRowPropShape.isRequired
};

DemoCreatedAtColumn.propTypes = {
  props: demoRowPropShape.isRequired
};

DemoManagerColumn.propTypes = {
  props: demoRowPropShape.isRequired
};

DemoTeamMemberColumn.propTypes = {
  props: demoRowPropShape.isRequired
};

DemoEmployeePrepColumn.propTypes = {
  props: PropTypes.shape({
    ...demoRowPropShape.propTypes,
    employeePrepStatus: PropTypes.number.isRequired
  }).isRequired
};

DemoStatusColumn.propTypes = {
  props: demoRowPropShape.isRequired
};
